/**
 * Norms Label Utility
 *
 * Provides a dynamically generated label for norms comparison sets,
 * reflecting the actual latest date of norms data rather than a hardcoded string.
 *
 * The norms automation pipeline runs monthly and writes a metadata document
 * to the Norms collection with type="metadata" containing the generation timestamp.
 * If no metadata is found, falls back to the current month/year.
 *
 * @module normsLabel
 */

import dataCache from './dataCache.js';
import { publishedNormYear } from './normsYear.js';

const MONTH_NAMES = [
	'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
	'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

let _cachedLabel = null;

/**
 * Format a "to <Month> <Year>" label for a date representing the end of the
 * latest month of norms data, capped at the published norm year.
 *
 * Early in a new calendar year (before the national Term-1 cutover) the current
 * year's norms are not yet published as a comparison, so the label must not claim
 * a period inside that year. In that case we cap the label at December of the
 * published (last completed) year, keeping the label honest about what schools are
 * actually being compared against.
 *
 * `now` is injectable so the cap can be tested deterministically (mirrors the
 * pure, date-injecting style of normsYear.js).
 *
 * @param {Date} d - Date representing the latest month of norms data.
 * @param {Date} [now=new Date()] - Reference date for the published-year cap.
 * @returns {string} e.g. "to Aug 2026" or, when capped, "to Dec 2026".
 */
export function formatCappedLabel(d, now = new Date()) {
	const published = publishedNormYear(now);
	if (d.getFullYear() > published) {
		// The computed period falls in a not-yet-published year: snap to the end
		// of the published year.
		return `to ${MONTH_NAMES[11]} ${published}`;
	}
	return `to ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Get the norms date label (e.g., "to Aug 2026").
 * Fetches from the Norms collection metadata on first call, then caches.
 *
 * @param {Object} db - DatabaseService instance (auth.db)
 * @returns {Promise<string>} The formatted label, e.g., "to Aug 2026"
 */
export async function getNormsDateLabel(db) {
	if (_cachedLabel) return _cachedLabel;

	try {
		// Try to fetch the metadata document written by the norms automation pipeline
		const metadata = await dataCache.get(
			'Norms',
			'read',
			{ type: 'metadata', key: 'last_generated' },
			async () => {
				return db.collection('Norms').find(
					{ type: 'metadata', key: 'last_generated' },
					{ _id: 0 }
				);
			},
			{ staleTime: 86400000 } // 24h
		);

		if (metadata && metadata.length > 0 && metadata[0].date) {
			const d = new Date(metadata[0].date);
			// Norms generated on the 1st include data up to the previous month
			d.setMonth(d.getMonth() - 1);
			_cachedLabel = formatCappedLabel(d);
			return _cachedLabel;
		}
	} catch (err) {
		// Metadata query failed — fall back
	}

	// Fallback: use previous month (norms include data up to end of previous month)
	const now = new Date();
	now.setMonth(now.getMonth() - 1);
	_cachedLabel = formatCappedLabel(now);
	return _cachedLabel;
}

/**
 * Get the norms date label synchronously.
 * Returns the cached value if available, otherwise a fallback based on current date.
 * Call getNormsDateLabel(db) at least once during app init to populate the cache.
 *
 * @returns {string} The formatted label, e.g., "to Aug 2026"
 */
export function getNormsDateLabelSync() {
	if (_cachedLabel) return _cachedLabel;
	const now = new Date();
	now.setMonth(now.getMonth() - 1);
	return formatCappedLabel(now);
}

/**
 * Format a norms comparison set label with the dynamic date.
 *
 * @param {string} name - The comparison set name (e.g., "Australia")
 * @returns {string} Formatted label (e.g., "Australia (to Aug 2026)")
 */
export function normsLabel(name) {
	return `${name} (${getNormsDateLabelSync()})`;
}
